import React from "react";

import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core";
import { Chip } from "../Chip";
import { TextField } from "../TextField";

const useChipInputStyles = makeStyles((theme) => ({
  inputRoot: {
    backgroundColor: "red",
    "&.MuiFilledInput-adornedStart": {
      paddingBottom: theme.spacing(1.5),
    },
  },
  tag: {
    marginTop: theme.spacing(1),
    backgroundColor: theme.palette.type === "light" ? "rgba(3, 158, 195, 0.15)" : "#039EC316",
  },
}));

interface SplitProps {
  splitCommas: boolean;
  splitWhitespace: boolean;
}
interface IProps extends Partial<SplitProps> {
  disabled?: boolean;
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  omitSelectionChips?: boolean;
  error?: boolean;
  removeAllQuotes?: boolean;
  isShowingValues?: boolean;
}

export function FreeSoloMultipleSelect({
  disabled,
  label,
  values,
  onChange,
  omitSelectionChips,
  isShowingValues,
  splitCommas = true,
  splitWhitespace = false,
  removeAllQuotes = false,
  error = false,
}: IProps) {
  const classes = useChipInputStyles();
  const regexForSplit = splitRegex({ splitCommas, splitWhitespace });

  return (
    <Autocomplete<string, true, true, true>
      disabled={disabled}
      classes={classes}
      multiple
      fullWidth
      freeSolo
      disableClearable={true}
      options={[]}
      value={values}
      onChange={(event, val, reason) => {
        if (reason === "create-option" && val.length) {
          const latestValue = val[val.length - 1];
          const newVal = removeAllQuotes ? latestValue.replace(/['"]/g, "") : latestValue;
          const splitVals = regexForSplit ? newVal.split(regexForSplit) : [newVal];
          const newVals = splitVals.map((v) => v.trim().replace(/\.$/, "")).filter(Boolean);
          const uniqueList = new Set([...values, ...newVals]);
          onChange(Array.from(uniqueList));
        } else {
          onChange(val);
        }
      }}
      renderTags={(value, getTagProps) =>
        isShowingValues ? (
          <span>{value.join(", ")}</span>
        ) : omitSelectionChips ? null : (
          value.map((option, index) => <Chip key={`${option}-${index}`} label={option} {...getTagProps({ index })} />)
        )
      }
      placeholder={label}
      renderInput={(params) => <TextField {...params} label={label} error={error} />}
    />
  );
}

const splitRegex = ({ splitCommas, splitWhitespace }: SplitProps): RegExp | undefined =>
  splitCommas && splitWhitespace
    ? WHITESPACE_AND_COMMAS_REGEXP
    : splitCommas
    ? COMMAS_ONLY_REGEXP
    : splitWhitespace
    ? WHITESPACE_ONLY_REGEXP
    : undefined;

const WHITESPACE_AND_COMMAS_REGEXP = /[,\s]+/;
const COMMAS_ONLY_REGEXP = /[,]+/;
const WHITESPACE_ONLY_REGEXP = /\s+/;
